import { Build } from "@mui/icons-material";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
} from "@mui/material";
import { MouseEvent, useState } from "react";
import {
  ArrayField,
  AutocompleteInput,
  BooleanField,
  CreateButton,
  Datagrid,
  EmailField,
  ExportButton,
  Form,
  List,
  ReferenceField,
  ReferenceInput,
  SingleFieldList,
  TextField,
  TextInput,
  TopToolbar,
  useNotify,
  useRecordContext,
} from "react-admin";
import { getSeeriApi } from "@/api";
import { SimpleChipField } from "../../components/SimpleChipField";
import { ADMINROLES, SUPPLIERS } from "../../config/resources";
import { useAdmin } from "../../hooks/admin";
import { CustomPagination } from "../shared/pagination";

const AdminFilter = [
  <TextInput source="email" alwaysOn />,
  <TextInput source="firstName" alwaysOn />,
  <TextInput source="lastName" alwaysOn />,
];

const AdminActions = () => {
  const { hasPermission } = useAdmin();

  return (
    <TopToolbar>
      {hasPermission("admin.create") && <CreateButton />}
      {hasPermission("admin.export") && <ExportButton />}
    </TopToolbar>
  );
};

// FixAdminClaimsButton posts to users-go to refresh the ADMIN custom claims
// on the row's Firebase UID. The supplier picker is optional — when set, the
// service also writes supplier_id + seeri_tenant_id so the admin token
// carries tenant context.
const FixAdminClaimsButton = () => {
  const record = useRecordContext<{ id: string }>();
  const notify = useNotify();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  if (!record?.id) return null;

  const handleOpen = (e: MouseEvent) => {
    e.stopPropagation();
    setOpen(true);
  };
  const handleClose = () => {
    if (loading) return;
    setOpen(false);
  };

  const submit = async (values: { supplierId?: string }) => {
    setLoading(true);
    try {
      const supplierId = values.supplierId?.trim();
      await getSeeriApi().post(
        `/api/users/v1/firebase/claims/${encodeURIComponent(record.id)}`,
        supplierId ? { supplierId } : {}
      );
      notify(
        supplierId
          ? "Rol ADMIN + tenant aplicados"
          : "Rol ADMIN aplicado",
        { type: "success" }
      );
      setOpen(false);
    } catch {
      notify("No se pudo aplicar el rol ADMIN", { type: "error" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Tooltip title="Reparar rol ADMIN">
        <IconButton size="small" onClick={handleOpen} disabled={loading}>
          <Build fontSize="small" />
        </IconButton>
      </Tooltip>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="xs"
        fullWidth
        onClick={(e) => e.stopPropagation()}
      >
        <DialogTitle>Reparar rol ADMIN</DialogTitle>
        <Form onSubmit={submit as any}>
          <DialogContent>
            <Stack spacing={2}>
              <DialogContentText>
                Se asignará el rol <b>ADMIN</b> en Firebase. Opcionalmente
                podés elegir un distribuidor para escribir también el tenant
                (<code>supplier_id</code> / <code>seeri_tenant_id</code>).
              </DialogContentText>
              <ReferenceInput
                source="supplierId"
                reference={SUPPLIERS}
                sort={{ field: "name", order: "ASC" }}
                perPage={50}
              >
                <AutocompleteInput
                  label="Distribuidor (opcional)"
                  optionText="name"
                  filterToQuery={(searchText: string) => ({
                    name: searchText,
                  })}
                  fullWidth
                />
              </ReferenceInput>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} disabled={loading}>
              Cancelar
            </Button>
            <Button type="submit" variant="contained" disabled={loading}>
              Aplicar
            </Button>
          </DialogActions>
        </Form>
      </Dialog>
    </>
  );
};

export const AdminList = () => {
  const { hasPermission } = useAdmin();

  if (!hasPermission("admin.list")) return null;

  return (
    <List
      perPage={20}
      filters={AdminFilter}
      pagination={<CustomPagination />}
      actions={<AdminActions />}
    >
      <Datagrid
        rowClick={hasPermission("admin.view") ? "show" : undefined}
        bulkActionButtons={false}
      >
        <TextField source="firstName" />
        <TextField source="lastName" />
        <ReferenceField
          source="roleId"
          reference={ADMINROLES}
          link={hasPermission("adminrole.view") ? "show" : false}
        >
          <TextField source="name" />
        </ReferenceField>
        <EmailField source="email" />
        <ArrayField source="countries">
          <SingleFieldList linkType={false}>
            <SimpleChipField />
          </SingleFieldList>
        </ArrayField>
        <BooleanField source="status" />
        {hasPermission("admin.update") && <FixAdminClaimsButton />}
      </Datagrid>
    </List>
  );
};
